import { useEffect, useState } from "react";
import styles from "../Styles/modules/sortableDataTable.module.css";

const SortableDataTable = ({
  columns,
  data,
  currentPage = 1,
  limit = 10,
  className = "",
  showSerial = true,
  sortable = true,
}) => {
  const [tableData, setTableData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [tooltip, setTooltip] = useState({ show: false, text: "", x: 0, y: 0 });

  useEffect(() => {
    setTableData(data || []);
  }, [data]);

  const handleSort = (key) => {
    if (!sortable) return;
    const column = columns.find((c) => c.key === key);
    if (column?.sortable === false) return;

    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sorted = [...tableData].sort((a, b) => {
      if (column?.compare) {
        return direction === "asc"
          ? column.compare(a, b)
          : column.compare(b, a);
      }

      const A = a[key];
      const B = b[key];

      if (A == null) return direction === "asc" ? -1 : 1;
      if (B == null) return direction === "asc" ? 1 : -1;

      if (typeof A === "string") {
        return direction === "asc" ? A.localeCompare(B) : B.localeCompare(A);
      }

      if (typeof A === "number") {
        return direction === "asc" ? A - B : B - A;
      }

      return direction === "asc"
        ? String(A).localeCompare(String(B))
        : String(B).localeCompare(String(A));
    });

    setSortConfig({ key, direction });
    setTableData(sorted);
  };

  const handleMouseEnter = (e, text) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      show: true,
      text: text,
      x: rect.left,
      y: rect.bottom - 5,
    });
  };

  const handleMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, show: false }));
  };

  const truncateText = (text, maxLength = 25) => {
    if (!text) return "-";
    const str = String(text);
    return str.length > maxLength ? str.substring(0, maxLength) + "..." : str;
  };

  return (
    <div className={`${styles.ledgerTableWrapper} ${className}`}>
      <div className={`${styles.tableResponsive} custom-scrollbar`}>
        <table className={styles.customTable}>
          <thead>
            <tr>
              {showSerial && <th>S.No.</th>}
              {columns
                .filter((c) => !c.hidden)
                .map((col) => (
                  <th
                    key={col.key}
                    onClick={sortable ? () => handleSort(col.key) : undefined}
                    style={{
                      cursor: col.sortable === false ? "default" : "pointer",
                      textAlign: col.align || "center",
                    }}
                  >
                    {col.label}
                    {sortConfig.key === col.key && col.sortable !== false && (
                      <span className="ms-1">
                        {sortConfig.direction === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                ))}
            </tr>
          </thead>

          <tbody>
            {tableData.map((row, index) => (
              <tr key={index}>
                {showSerial && (
                  <td className="text-center">
                    {(currentPage - 1) * limit + index + 1}
                  </td>
                )}
                {columns
                  .filter((c) => !c.hidden)
                  .map((col) => {
                    const originalValue = row[col.key];
                    const cellValue = col.render
                      ? col.render(row)
                      : (originalValue ?? "-");
                    const isTruncated =
                      col.showTooltip &&
                      String(originalValue || "").length > 25;

                    const displayValue = col.showTooltip
                      ? truncateText(originalValue)
                      : cellValue;

                    return (
                      <td
                        key={col.key}
                        className={`text-${col.align || "center"}`}
                        style={{
                          cursor: isTruncated ? "pointer" : "default",
                        }}
                        onMouseMove={
                          isTruncated
                            ? (e) => handleMouseEnter(e, originalValue)
                            : undefined
                        }
                        onMouseLeave={
                          isTruncated ? handleMouseLeave : undefined
                        }
                      >
                        {displayValue}
                      </td>
                    );
                  })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Simple Tooltip */}
      {tooltip.show && tooltip.text && (
        <div
          style={{
            position: "fixed",
            top: tooltip.y,
            left: tooltip.x,
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "6px",
            padding: "6px 10px",
            zIndex: 9999,
            maxWidth: "300px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            fontSize: "0.85rem",
            wordBreak: "break-word",
            pointerEvents: "none",
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
};

export default SortableDataTable;
